import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpRightFromSquare,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "../i18n/I18nProvider";
import { Plan } from "../types";
import { buildSigningFrameUrl } from "../utils/signingLink";
import {
  ConsentStatus,
  LeadWizardMetadata,
  WizardPrimaryApplicant,
} from "../types/leadWizard";

interface PlanSummaryPageProps {
  metadata: LeadWizardMetadata;
  plan: Plan;
  primary: WizardPrimaryApplicant;
  consentStatus: ConsentStatus;
  language?: string | null;
  signingLink?: string | null;
  consentHtml?: string | null;
  onBack: () => void;
  emailRequired?: boolean;
  onConfirm: (email?: string) => void;
  onCheckConsent?: () => void;
  isSubmitting?: boolean;
  isCheckingConsent?: boolean;
}

const formatCurrency = (value: number | null | undefined) => {
  if (!value && value !== 0) {
    return "—";
  }
  return `$${Number(value).toLocaleString()}`;
};

export const PlanSummaryPage: React.FC<PlanSummaryPageProps> = ({
  metadata,
  plan,
  primary,
  consentStatus,
  language,
  signingLink,
  consentHtml,
  emailRequired = false,
  onBack,
  onConfirm,
  onCheckConsent,
  isSubmitting = false,
  isCheckingConsent = false,
}) => {
  const { t } = useTranslation();

  const [emailInput, setEmailInput] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const needsEmail = emailRequired && !primary.email?.trim();
  const trimmedEmail = emailInput.trim();
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  const showEmailError = emailTouched && !isEmailValid;

  const householdMembers = metadata.members || [];
  const householdIncome = metadata.household.income;

  return (
    <div className="card summary confirmation-card">
      <div className="card-header">
        {consentStatus === "NOT_REQUESTED" && (
          <button
            type="button"
            className="button button-secondary"
            onClick={onBack}
          >
            {t("buttons.back")}
          </button>
        )}
        <p className="section-helper">{t("wizard.summary.helper")}</p>
      </div>
      <div className="card-body ">
        <section className="form-section">
          <h5>{t("detailsPage.selectedPlanTitle")}</h5>
          <div className="selected-plan">
            <div>
              <h5 className="title-plan">{plan.name}</h5>
              <p className="plan-carrier">{plan.carrier}</p>
            </div>
            <div className="plan-price-selected">
              <span className="meta-label">
                {t("planSelection.monthlyPremium")}
              </span>
              <strong>${plan.monthlyPremium.toFixed(2)}</strong>
            </div>
          </div>
        </section>

        <section className="summary-section">
          <h5>{t("wizard.summary.householdTitle")}</h5>
          <ul className="summary-list">
            <li>
              <strong>{t("wizard.summary.zipCode")}</strong>
              <span>{metadata.household.zipCode ?? "—"}</span>
            </li>
            <li>
              <strong>{t("wizard.summary.county")}</strong>
              <span>
                {metadata.household.countyName ??
                  metadata.household.countyFips ??
                  "—"}
              </span>
            </li>
            <li>
              <strong>{t("wizard.summary.income")}</strong>
              <span>{formatCurrency(householdIncome)}</span>
            </li>
          </ul>

          <div className="members-list">
            {householdMembers.map((member, index) => (
              <div key={`${member.role}-${index}`} className="member-row">
                <strong>
                  {`${member.firstName} ${member.lastName}`.trim()}
                </strong>
                <span>
                  {t(`wizard.members.roles.${member.role.toLowerCase()}`)} ·{" "}
                  {member.birthDate}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="summary-section">
          <h4>{t("wizard.summary.primaryTitle")}</h4>
          <ul className="summary-list">
            <li>
              <strong>{t("wizard.primary.firstNameLabel")}</strong>
              <span>{primary.firstName}</span>
            </li>
            <li>
              <strong>{t("wizard.primary.lastNameLabel")}</strong>
              <span>{primary.lastName}</span>
            </li>
            <li>
              <strong>{t("wizard.primary.phoneLabel")}</strong>
              <span>{primary.phone}</span>
            </li>
            <li>
              <strong>{t("wizard.primary.emailLabel")}</strong>
              <span>{primary.email || "—"}</span>
            </li>
          </ul>
          {needsEmail && consentStatus === "NOT_REQUESTED" && (
            <div className="form-group">
              <label htmlFor="summaryEmail">{t("wizard.primary.emailLabel")}</label>
              <input
                id="summaryEmail"
                type="email"
                value={emailInput}
                placeholder={t("wizard.primary.emailPlaceholder")}
                onChange={(event) => setEmailInput(event.target.value)}
                onBlur={() => setEmailTouched(true)}
              />
              <span className={showEmailError ? "form-error" : "section-helper"}>
                {showEmailError
                  ? t("wizard.primary.errors.emailInvalid")
                  : t("wizard.summary.emailRequired")}
              </span>
            </div>
          )}
        </section>

        {signingLink && (
          <>
            <section className="summary-section">
              <div className="consent-summary-header">
                <h4>{t("wizard.summary.consentTitle")}</h4>
                <div
                  className={`consent-status consent-${consentStatus.toLowerCase()}`}
                >
                  {signingLink && (
                    <a
                      className="signing-link"
                      href={signingLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t("wizard.summary.openSignature")}
                      <FontAwesomeIcon
                        icon={faUpRightFromSquare}
                        className="signing-link-icon"
                      />
                    </a>
                  )}
                </div>
              </div>
              <div>{t("wizard.summary.consentSubTitle")}</div>
              <div className="signing-frame-container">
                {consentStatus !== "SIGNED" && (
                  <div className="signing-frame">
                    <iframe
                      src={buildSigningFrameUrl(signingLink, language)}
                      title="Consent"
                      width="100%"
                      height="350"
                      style={{ border: "none" }}
                    />
                  </div>
                )}
                {consentHtml && consentStatus !== "SIGNED" && (
                  <div
                    className="consent-preview"
                    dangerouslySetInnerHTML={{ __html: consentHtml }}
                  />
                )}
              </div>
            </section>
          </>
        )}

        <div className="form-actions">
          {consentStatus === "NOT_REQUESTED" && (
            <button
              type="button"
              className="button button-primary"
              onClick={() => {
                if (needsEmail && !isEmailValid) {
                  setEmailTouched(true);
                  return;
                }
                onConfirm(needsEmail ? trimmedEmail : undefined);
              }}
              disabled={isSubmitting || (needsEmail && !isEmailValid)}
            >
              {isSubmitting
                ? t("wizard.summary.sending")
                : t("wizard.summary.confirmButton")}
            </button>
          )}
          {consentStatus === "PENDING" && (
            <button
              type="button"
              className="button button-primary"
              onClick={onCheckConsent}
              disabled={consentStatus === "PENDING" || isCheckingConsent}
            >
              {isCheckingConsent
                ? t("wizard.summary.checkingConsent")
                : t("wizard.summary.confirmButton")}
            </button>
          )}
        </div>

        {consentStatus !== "SIGNED" && signingLink && onCheckConsent && (
          <section className="summary-section">
            <button
              type="button"
              className="button button-secondary"
              onClick={onCheckConsent}
              disabled={isCheckingConsent}
            >
              {isCheckingConsent
                ? t("wizard.summary.checkingConsent")
                : t("wizard.summary.checkConsent")}
            </button>
          </section>
        )}

        {consentStatus === "SIGNED" && (
          <section className="summary-section consent-signed">
            <div className="consent-status consent-signed">
              <FontAwesomeIcon icon={faCircleCheck} />
              <div className="consent-signed-copy">
                <strong>{t("wizard.summary.consentSignedTitle")}</strong>
                <span>{t("wizard.summary.consentSignedSubtitle")}</span>
              </div>
            </div>
            <span className="close-page">{t("wizard.summary.closePage")}</span>
          </section>
        )}
      </div>
    </div>
  );
};
